/**
 * Server-paged reseller list (TMMPDM-0015) — follows `Tenants.tsx`. TMMPDM-0017: read from
 * tmm-api `GET /v1/admin/resellers` (saas-auth's page passed through: `content`).
 * TMMPDM-0035: search, status and page live in the URL; rows carry the list back to the
 * reseller's breadcrumb.
 * TMMPDM-0040: `create=1` in the URL opens Create reseller (the palette's New reseller).
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { useReturnTo } from '@/hooks/useReturnTo';
import {
  oneOf,
  positiveInt,
  text,
  useSearchDraft,
  useUrlState,
  type UrlSchema,
} from '@/hooks/useUrlState';
import { adminApi } from '@/api/tmm/admin';
import { isActingAdminRevoked } from '@/api/errors';
import {
  EditDetailsDialog,
  EmptyState,
  ErrorState,
  PageHeader,
  fieldEditable,
} from '@/components/page';
import type { ResellerPage, ResellerStatus, ResellerStandingState } from '@/api/tmm/types';
import { PartnersTable } from './PartnersTable';
import { NewPartnerDialog } from './NewPartnerDialog';

type StatusFilter = 'ALL' | ResellerStatus;

const PAGE_SIZE = 20;

/** TMMPDM-0035: the view lives in the URL — `q`, `status` and the 1-based `page`. */
type ListView = {
  q: string;
  status: StatusFilter;
  page: number;
  standing: 'ALL' | ResellerStandingState;
  /** TMMPDM-0040: `create=1` (the palette's New reseller) opens the create dialog. */
  create: '1' | undefined;
};
const VIEW: UrlSchema<ListView> = {
  q: { default: '', parse: text },
  standing: {
    default: 'ALL',
    parse: oneOf(['ALL', 'GOOD', 'PROBATION', 'DEACTIVATION_DUE', 'DEACTIVATED'] as const),
  },
  status: { default: 'ALL', parse: oneOf<StatusFilter>(['ALL', 'ACTIVE', 'SUSPENDED']) },
  page: { default: 1, parse: positiveInt },
  create: { default: undefined, parse: oneOf(['1'] as const) },
};
const DEFAULT_VIEW: ListView = {
  standing: 'ALL',
  q: '',
  status: 'ALL',
  page: 1,
  create: undefined,
};

export function Partners() {
  const { t } = useCopy();
  const { linkState } = useReturnTo();
  const [view, setView] = useUrlState(VIEW);
  const page = view.page - 1;
  const query = view.q;
  const statusFilter = view.status;
  const search = useSearchDraft(query, (q) => setView({ q }, { history: 'replace' }));
  const [reloadToken, setReloadToken] = useState(0);
  const [detailsId, setDetailsId] = useState<number | null>(null);
  const [detailsToken, setDetailsToken] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const requestKey = `${page}:${query}:${statusFilter}:${view.standing}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    result: ResellerPage | null;
    failure: unknown;
  }>({ requestKey: null, result: null, failure: null });

  useEffect(() => {
    let cancelled = false;
    const read = async () => {
      const filters = {
        ...(query ? { q: query } : {}),
        ...(statusFilter === 'ALL' ? {} : { status: statusFilter }),
      };
      return adminApi.listResellers({
        ...filters,
        ...(view.standing === 'ALL' ? {} : { standing: view.standing }),
        page,
        size: PAGE_SIZE,
      });
    };
    read()
      .then((result) => {
        if (!cancelled) setResponse({ requestKey, result, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setResponse({ requestKey, result: null, failure: failure ?? 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [page, query, statusFilter, view.standing, requestKey]);

  const current = response.requestKey === requestKey;
  const loading = !current;
  const failure = current ? response.failure : null;
  const resultPage = current ? response.result : null;
  const rows = resultPage?.content ?? [];
  const detailsReseller = rows.find((row) => row.id === detailsId) ?? null;
  const pageNumber = (resultPage?.page ?? page) + 1;
  const totalPages = resultPage?.totalPages ?? 0;
  // A page past the end (a stale or hand-edited link) is narrowing too: Clear goes back to page 1.
  const filtered =
    view.standing !== 'ALL' || query !== '' || statusFilter !== 'ALL' || view.page > 1;
  const clearFilters = () => {
    search.discard();
    setView(DEFAULT_VIEW);
  };
  const empty = filtered ? (
    <EmptyState
      title={
        query
          ? t('partners.emptySearch', 'No partners match your search.')
          : t('partners.empty', 'No partners yet.')
      }
      filtered={{ onClear: clearFilters }}
    />
  ) : (
    <EmptyState title={t('partners.empty', 'No partners yet.')} />
  );

  // TMMPDM-0040: opened from the URL, closing drops `create=1` in place (replace, page kept), and a
  // save replaces the `create=1` entry so Back never reopens the dialog.
  const openedFromUrl = view.create === '1';
  const closeCreate = () => {
    setCreateOpen(false);
    if (openedFromUrl) setView({ create: undefined, page: view.page }, { history: 'replace' });
  };

  return (
    <Box>
      <PageHeader
        title={t('page.partners.title', 'Partners')}
        subtitle={t('partners.subtitle', 'Partners and their businesses, together in one place.')}
        primaryAction={{
          label: t('partners.new', 'New partner…'),
          onClick: () => setCreateOpen(true),
        }}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 2, alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between' }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            value={search.value}
            onChange={(event) => search.onChange(event.target.value)}
            label={t('partners.search', 'Search partners')}
            placeholder={t('partners.searchPlaceholder', 'Search partner or slug')}
            helperText={t('partners.searchHint', 'Searches partner name and slug')}
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel id="reseller-status-filter-label">
              {t('resellers.statusFilter.label', 'Status')}
            </InputLabel>
            <Select
              labelId="reseller-status-filter-label"
              value={statusFilter}
              label={t('resellers.statusFilter.label', 'Status')}
              onChange={(event) => setView({ status: event.target.value as StatusFilter })}
            >
              <MenuItem value="ALL">{t('resellers.statusFilter.all', 'All')}</MenuItem>
              <MenuItem value="ACTIVE">{t('resellers.statusFilter.active', 'Active')}</MenuItem>
              <MenuItem value="SUSPENDED">
                {t('resellers.statusFilter.suspended', 'Suspended')}
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            select
            size="small"
            label={t('resellers.col.standing', 'Standing')}
            value={view.standing}
            onChange={(event) => setView({ standing: event.target.value as ListView['standing'] })}
            sx={{ minWidth: 190 }}
          >
            {['ALL', 'GOOD', 'PROBATION', 'DEACTIVATION_DUE', 'DEACTIVATED'].map((state) => (
              <MenuItem key={state} value={state}>
                {state === 'ALL'
                  ? t('resellers.statusFilter.all', 'All')
                  : t(`resellers.standing.${state}`, state)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>

      {outcomeUnknown ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setOutcomeUnknown(false)}>
          {t(
            'common.outcomeUnknown',
            "We couldn't confirm the change. The screen has been reloaded — check before trying again.",
          )}
        </Alert>
      ) : null}

      {failure ? (
        <ErrorState
          error={failure}
          message={t('resellers.loadError', 'Could not load resellers.')}
          {...(isActingAdminRevoked(failure)
            ? {}
            : { onRetry: () => setReloadToken((n) => n + 1) })}
        />
      ) : (
        <PartnersTable
          resellers={rows}
          loading={loading}
          empty={empty}
          linkState={linkState}
          onEditDetails={(reseller) => setDetailsId(reseller.id)}
        />
      )}

      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 2, alignItems: 'center', justifyContent: 'flex-end' }}
      >
        <Button
          variant="outlined"
          disabled={loading || (resultPage?.page ?? page) <= 0}
          onClick={() => setView({ page: Math.max(1, view.page - 1) })}
        >
          {t('tenants.pagination.previous', 'Previous')}
        </Button>
        <Typography variant="body2" aria-live="polite">
          {t('tenants.pagination.pageOf', 'Page {{page}} of {{total}}', {
            page: totalPages === 0 ? 0 : pageNumber,
            total: totalPages,
          })}
        </Typography>
        <Button
          variant="outlined"
          disabled={loading || totalPages === 0 || (resultPage?.page ?? page) + 1 >= totalPages}
          onClick={() => setView({ page: view.page + 1 })}
        >
          {t('tenants.pagination.next', 'Next')}
        </Button>
      </Stack>

      <EditDetailsDialog
        open={detailsReseller !== null}
        entityLabel={t('components.editDetails.entity.reseller', 'reseller')}
        fields={
          detailsReseller && fieldEditable(detailsReseller.editableFields, 'name')
            ? [
                {
                  key: 'name',
                  label: t('resellers.create.nameLabel', 'Name'),
                  value: detailsReseller.name,
                  required: true,
                  helperText: t(
                    'resellers.overview.renameHelper',
                    'The reseller brand follows the new name unless it was customised.',
                  ),
                },
              ]
            : []
        }
        version={null}
        validate={(values) => {
          if ((values.name ?? '').trim().length > 200) {
            return {
              name: t('resellers.create.nameTooLong', 'Name must be 200 characters or fewer.'),
            };
          }
          return {};
        }}
        onClose={() => setDetailsId(null)}
        onReload={() => {
          setReloadToken((n) => n + 1);
          setDetailsToken((n) => n + 1);
        }}
        reloadToken={detailsToken}
        onOutcomeUnknown={() => {
          setOutcomeUnknown(true);
          setReloadToken((n) => n + 1);
        }}
        onSubmit={async (changed) => {
          if (!detailsReseller || changed.name === undefined) return;
          await adminApi.updateReseller(detailsReseller.id, { name: changed.name.trim() });
        }}
      />

      <NewPartnerDialog
        open={createOpen || openedFromUrl}
        onClose={closeCreate}
        onOutcomeUnknown={() => {
          // The create may have happened: re-read the list, never re-POST.
          setCreateOpen(false);
          setOutcomeUnknown(true);
          setView(
            { page: 1, create: undefined },
            openedFromUrl ? { history: 'replace' } : undefined,
          );
          setReloadToken((n) => n + 1);
        }}
      />
    </Box>
  );
}
